import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/query-client'
import { AuthProvider, useAuth } from '@/lib/AuthContext'
import { Toaster } from '@/components/ui/sonner'
import { AppLayout } from '@/components/layout/AppLayout'
import { PublicLayout } from '@/components/layout/PublicLayout'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { OfflineBanner } from '@/components/OfflineBanner'
import { OnboardingWizard } from '@/components/OnboardingWizard'
import { LocaleGate } from '@/components/LocaleGate'

// Public portal pages
const LandingPage = lazy(() => import('@/pages/public/LandingPage').then((m) => ({ default: m.LandingPage })))
const HomePage = lazy(() => import('@/pages/public/HomePage').then((m) => ({ default: m.HomePage })))
const DirectoryPage = lazy(() => import('@/pages/public/DirectoryPage').then((m) => ({ default: m.DirectoryPage })))
const EcosystemMapPage = lazy(() => import('@/pages/public/EcosystemMapPage').then((m) => ({ default: m.EcosystemMapPage })))
const ForumPage = lazy(() => import('@/pages/ForumPage').then((m) => ({ default: m.ForumPage })))
const ForumTopicPage = lazy(() => import('@/pages/ForumTopicPage').then((m) => ({ default: m.ForumTopicPage })))
const InnovationPage = lazy(() => import('@/pages/public/InnovationPage').then((m) => ({ default: m.InnovationPage })))
const RegisterOrgPage = lazy(() => import('@/pages/public/RegisterOrgPage').then((m) => ({ default: m.RegisterOrgPage })))
const ContactPage = lazy(() => import('@/pages/public/ContactPage').then((m) => ({ default: m.ContactPage })))
const ConnectionsPage = lazy(() => import('@/pages/public/ConnectionsPage').then((m) => ({ default: m.ConnectionsPage })))

// App (authenticated) pages
const LoginPage = lazy(() => import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })))
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })))
const ProductsPage = lazy(() => import('@/pages/ProductsPage').then((m) => ({ default: m.ProductsPage })))
const ProductDetailPage = lazy(() => import('@/pages/ProductDetailPage').then((m) => ({ default: m.ProductDetailPage })))
const BiofactoryWizardPage = lazy(() => import('@/pages/BiofactoryWizardPage').then((m) => ({ default: m.BiofactoryWizardPage })))
const FieldsPage = lazy(() => import('@/pages/FieldsPage').then((m) => ({ default: m.FieldsPage })))
const FieldActivityPage = lazy(() => import('@/pages/FieldActivityPage').then((m) => ({ default: m.FieldActivityPage })))
const BatchesPage = lazy(() => import('@/pages/BatchesPage').then((m) => ({ default: m.BatchesPage })))
const BatchDetailPage = lazy(() => import('@/pages/BatchDetailPage').then((m) => ({ default: m.BatchDetailPage })))
const TimelinePage = lazy(() => import('@/pages/TimelinePage').then((m) => ({ default: m.TimelinePage })))
const TraceabilityPage = lazy(() => import('@/pages/TraceabilityPage').then((m) => ({ default: m.TraceabilityPage })))
const QRScannerPage = lazy(() => import('@/pages/QRScannerPage').then((m) => ({ default: m.QRScannerPage })))
const TracePublicPage = lazy(() => import('@/pages/TracePublicPage').then((m) => ({ default: m.TracePublicPage })))
const CompliancePage = lazy(() => import('@/pages/CompliancePage').then((m) => ({ default: m.CompliancePage })))
const AuditPage = lazy(() => import('@/pages/AuditPage').then((m) => ({ default: m.AuditPage })))
const ProfilePage = lazy(() => import('@/pages/ProfilePage').then((m) => ({ default: m.ProfilePage })))
const AdminOrgsPage = lazy(() => import('@/pages/admin/AdminOrgsPage').then((m) => ({ default: m.AdminOrgsPage })))
const AdminContentPage = lazy(() => import('@/pages/admin/AdminContentPage').then((m) => ({ default: m.AdminContentPage })))
const AdminValidationPage = lazy(() => import('@/pages/admin/AdminValidationPage').then((m) => ({ default: m.AdminValidationPage })))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })))

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" aria-label="Carregando" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return (
    <ErrorBoundary>
      {children}
      <OnboardingWizard />
    </ErrorBoundary>
  )
}

export function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* ═══ Public Portal (no auth) ═══ */}
        {/* Wrapper de idioma: `/:lang?` cobre a raiz (PT) e os prefixos ja/en/es
            sem duplicar a árvore de rotas. O ranking do react-router 6 mantém
            /login, /app/* e /trace/:batchNumber (segmentos estáticos) fora daqui. */}
        <Route path="/:lang?" element={<LocaleGate />}>
          <Route element={<PublicLayout />}>
            {/* Landing MVP por categorias assume a raiz; a HomePage antiga fica em /inicio */}
            <Route index element={<LandingPage />} />
            <Route path="inicio" element={<HomePage />} />
            <Route path="diretorio" element={<DirectoryPage />} />
            <Route path="mapa" element={<EcosystemMapPage />} />
            <Route path="forum" element={<ForumPage />} />
            <Route path="forum/:id" element={<ForumTopicPage />} />
            <Route path="inovacao" element={<InnovationPage />} />
            <Route path="cadastrar" element={<RegisterOrgPage />} />
            <Route path="conexoes" element={<ConnectionsPage />} />
            <Route path="contato" element={<ContactPage />} />
          </Route>
        </Route>

        {/* ═══ Auth ═══ */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/app/dashboard" replace /> : <LoginPage />}
        />

        {/* ═══ App (authenticated SaaS) ═══ */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/app/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="products" element={<ProductsPage />} />
          <Route path="products/:id" element={<ProductDetailPage />} />
          <Route path="fields" element={<FieldsPage />} />
          <Route path="fields/activity/new" element={<FieldActivityPage />} />
          <Route path="batches" element={<BatchesPage />} />
          <Route path="batches/:id" element={<BatchDetailPage />} />
          <Route path="timeline" element={<TimelinePage />} />
          <Route path="traceability" element={<TraceabilityPage />} />
          <Route path="scanner" element={<QRScannerPage />} />
          <Route path="compliance" element={<CompliancePage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="biofactory/new" element={<BiofactoryWizardPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="admin/organizations" element={<AdminOrgsPage />} />
          <Route path="admin/content" element={<AdminContentPage />} />
          <Route path="admin/validation" element={<AdminValidationPage />} />
        </Route>

        {/* ═══ Public standalone routes ═══ */}
        <Route path="/trace/:batchNumber" element={<TracePublicPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  )
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
          <Toaster />
          <OfflineBanner />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  )
}
